import React from "react";
import { motion } from "framer-motion";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Button, Reveal } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import { fadeUp, viewport } from "@/lib/motion";
import TrustGraph from "@/components/TrustGraph";
import {
  ControlPlaneHero,
  AssuranceLifecycle,
  EvidenceCore,
  CapabilityLayers,
  SignalToProof,
  RoleConsole,
  ContinuousAssurance,
  CertificateShowcase,
  VerificationDemo,
} from "@/components/PlatformVisuals";

const DEMO_CERT_FULL = {
  certificate_id: "CERT-DEMO-000148", organization: "Demo Organization", ai_system: "Customer Service Agent",
  assurance_level: "Governed", status: "valid", issued_date: "2026-08-14", valid_until: "2027-08-14",
  assessment_type: "AI Security Assessment + Governance Review", system_type: "Customer-facing LLM agent",
  frameworks: ["ISO/IEC 42001", "NIST AI RMF", "OWASP LLM Top 10", "EU AI Act"],
  testing_engines: ["Prompt Injection", "Data Leakage", "Jailbreak Resistance", "Tool & Agent Abuse", "PII Handling"],
  test_coverage: [
    { category: "Prompt injection", result: "pass" },
    { category: "Sensitive data leakage", result: "pass" },
    { category: "Jailbreak resistance", result: "pass" },
    { category: "Tool & permission abuse", result: "review" },
    { category: "Output safety", result: "pass" },
  ],
  findings_summary: { critical: 0, high: 0, medium: 2, resolved: 3 },
  trust_score: 87, verification_hash: "a7f3c9e21b4d88f6e0c5a91d7b2e4f80c6a1d93e", sample: true,
};

export default function Platform() {
  return (
    <>
      {/* -------------------- HERO -------------------- */}
      <section className="relative overflow-hidden pb-14 pt-28 sm:pb-20 sm:pt-36 md:pb-28 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-16 left-1/3 h-[260px] w-[420px] -translate-x-1/2 rounded-full bg-brand/[0.06] blur-[90px] sm:-top-24 sm:h-[420px] sm:w-[820px] sm:blur-[120px]" />
        <Container className="relative">
          <div className="grid items-center [&>*]:min-w-0 gap-10 sm:gap-12 lg:grid-cols-[1fr_0.95fr] lg:gap-10">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show"><Eyebrow>Platform</Eyebrow></motion.div>
              <DisplayHeading as="h1" className="mt-6 max-w-2xl text-[34px] leading-[1.06] sm:mt-7 sm:text-5xl md:text-6xl">
                <MaskedLinesInView lines={["One control plane", "for AI trust."]} />
              </DisplayHeading>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-8 max-w-xl">
                <BodyCopy>
                  Discover, test, govern and assure AI systems from one connected platform — with every assessment, control, decision and outcome contributing to a shared evidence layer.
                </BodyCopy>
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:mt-9 sm:max-w-none sm:flex-row sm:flex-wrap sm:gap-4">
                <Button to="/contact" variant="primary" data-testid="platform-hero-demo" className="uppercase tracking-[0.12em]">Book a Demo</Button>
                <Button href="#explore" variant="outline" data-testid="platform-hero-explore">Explore the Platform</Button>
              </motion.div>
            </div>
            <div className="relative"><ControlPlaneHero /></div>
          </div>
        </Container>
      </section>

      {/* -------------------- LIFECYCLE -------------------- */}
      <Section id="explore" className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="brand">The assurance lifecycle</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Test → govern → evidence → assure → prove.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-5 max-w-2xl">Ten connected stages, one continuous loop. Nothing is lost between a test and a certificate.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-8 sm:mt-12"><AssuranceLifecycle /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- EVIDENCE CORE -------------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>One connected system</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl md:text-5xl">Everything connects through evidence.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Security findings, risks, controls, framework requirements, decisions and assurance outcomes should not live in separate silos. Meira connects them through one evidence and trust layer.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-10 sm:mt-14"><EvidenceCore /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- CAPABILITY LAYERS -------------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="brand">Connected capabilities</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Connected capabilities. One evidence layer.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-5 max-w-2xl">Pick a capability to trace how it flows into the shared evidence layer — one platform, not twelve isolated tools.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-8 sm:mt-12"><CapabilityLayers /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- SIGNAL TO PROOF -------------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow tone="brand">From signal to proof</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl md:text-5xl">See how one finding moves through Meira.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">A single security finding becomes a risk, a control, remediation, evidence — and finally verifiable proof. Demonstration data.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-10 sm:mt-14"><SignalToProof /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- TRUST GRAPH -------------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="brand">Meira Trust Graph</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl md:text-5xl">See why something is trusted — not just its score.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Meira connects AI systems, findings, risks, controls, frameworks and evidence so assurance outcomes stay traceable to what actually supports them.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-8 sm:mt-12"><TrustGraph /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- ROLE CONSOLE -------------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>Roles</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Designed for the people who answer for AI.</DisplayHeading>
          <Reveal i={1}><div className="mt-8 sm:mt-12"><RoleConsole /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- CONTINUOUS ASSURANCE -------------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="brand">Continuous assurance</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Trust changes when AI changes.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-5 max-w-2xl">Models evolve. Agents gain capabilities. Findings are remediated. Evidence ages. Assurance should stay connected to the current state of the AI system.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-8 sm:mt-12"><ContinuousAssurance /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- PROOF LAYER (certificate) -------------------- */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow tone="brand">The proof layer</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl md:text-5xl">From assurance to something others can verify.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Meira turns assessment outcomes, governance context and supporting evidence into a verifiable assurance record tied to the AI system it represents.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-10 sm:mt-14"><CertificateShowcase cert={DEMO_CERT_FULL} /></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- PUBLIC VERIFICATION -------------------- */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="valid">Independently verifiable</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Don’t take the certificate at face value. Verify it.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Anyone with the certificate ID or QR code can check the assurance record’s authenticity and current status — without a Meira account.</BodyCopy></Reveal>
          <Reveal i={2}><div className="mt-8 sm:mt-12"><VerificationDemo /></div></Reveal>
          <Reveal i={3}><div className="mt-8"><Button to="/verify" variant="outline" data-testid="platform-verify-cta">Open the public verification page →</Button></div></Reveal>
        </Container>
      </Section>

      {/* -------------------- FINAL CTA -------------------- */}
      <section className="relative overflow-hidden py-20 sm:py-28 md:py-36">
        <div className="pointer-events-none absolute inset-0 tech-grid opacity-40" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[260px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/[0.05] blur-[100px] sm:h-[380px] sm:w-[760px] sm:blur-[130px]" />
        <Container className="relative text-center">
          <div className="mx-auto mb-8 flex max-w-md flex-wrap items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em]" aria-hidden>
            {["Test", "Govern", "Evidence", "Assure", "Prove"].map((s, i, a) => (
              <React.Fragment key={s}>
                <span className={i === a.length - 1 ? "text-valid" : i >= 2 ? "text-brand" : "text-content-muted"}>{s}</span>
                {i < a.length - 1 && <span className="text-content-faint">→</span>}
              </React.Fragment>
            ))}
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="mx-auto flex justify-center"><Eyebrow tone="brand">See Meira in action</Eyebrow></motion.div>
          <DisplayHeading className="mx-auto mt-7 max-w-3xl text-3xl leading-tight sm:text-4xl md:text-5xl">
            <MaskedLinesInView lines={["From AI system", "to verifiable assurance."]} />
          </DisplayHeading>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={2}>
            <BodyCopy className="mx-auto mt-6 max-w-xl">See how Meira connects AI security, governance, evidence and assurance across the AI lifecycle.</BodyCopy>
            <div className="mx-auto mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:mt-9 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4">
              <Button to="/contact" variant="primary" data-testid="platform-cta-demo" className="uppercase tracking-[0.12em]">Book a Demo</Button>
              <Button to="/verify" variant="outline" data-testid="platform-cta-verify">Verify an Assurance Record</Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
}
